<template>
  <div class="product-details">
    <!-- Product Title -->
    <h1 class="product-title">{{ data.title }}</h1>
    <!-- Dress Category -->
    <p class="dress-category">
      <strong>Dress Category:</strong> {{ data.clothingType }}
    </p>
    <!-- Display Primary Color and All Colors on the same line -->
    <div class="color-information" v-if="data.primaryColor || (data.allColors && data.allColors.length)">
      <div class="color-item" v-if="data.primaryColor">
        <strong>Primary Color:</strong> {{ data.primaryColor }}
      </div>
      <div class="color-item" v-if="data.allColors && data.allColors.length">
        <strong>All Colors:</strong> {{ data.allColors.join(', ') }}
      </div>
    </div>
    <!-- Color Selection -->
    <div class="product-color" v-if="availableColors && availableColors.length">
      <label>Available Colors:</label>
      <div class="color-swatches">
        <div
          v-for="(colorValue, index) in availableColors"
          :key="index"
          class="color-swatch"
          :style="getSwatchStyle(colorValue)"
          @click="selectColor(colorValue)"
        >
          <span v-if="localSelectedColor === colorValue" class="swatch-selected">✓</span>
        </div>
      </div>
    </div>
    <!-- Size Selection -->
    <div class="product-size" v-if="availableSizes && availableSizes.length">
      <label>Available Sizes:</label>
      <div class="size-options">
        <button
          v-for="(size, index) in availableSizes"
          :key="index"
          :class="{ selected: localSelectedSize === size }"
          @click="selectSize(size)"
        >
          {{ size }}
        </button>
      </div>
    </div>
    <!-- Bullet Points (Product Features) -->
    <div class="product-bullet-points">
      <h2>Key Features</h2>
      <div v-if="data.bulletPoints && data.bulletPoints.length">
        <ul>
          <li v-for="(point, index) in data.bulletPoints" :key="index">{{ point }}</li>
        </ul>
      </div>
      <div v-else>
        <p>No key features available.</p>
      </div>
    </div>
    <!-- Description -->
    <div class="product-description">
      <h2>Description</h2>
      <div v-if="data.description">
        <p v-html="data.description"></p>
      </div>
      <div v-else>
        <p>No description available.</p>
      </div>
    </div>
    <!-- Specifications -->
    <div class="product-specifications" v-if="Object.keys(data.specifications).length">
      <h2>Product Specifications</h2>
      <table>
        <tbody>
          <tr v-for="(value, key) in data.specifications" :key="key">
            <td class="spec-key">{{ key }}</td>
            <td class="spec-value">
              <div v-if="key === 'Care Instructions' && hasCareInstructions">
                <div v-if="careInstructionLanguages.length > 1">
                  <!-- Multiple languages -->
                  <label for="care-language">Language:</label>
                  <select v-model="selectedCareLanguage" id="care-language">
                    <option
                      v-for="lang in careInstructionLanguages"
                      :key="lang"
                      :value="lang"
                    >
                      {{ lang }}
                    </option>
                  </select>
                  <ul>
                    <li
                      v-for="(instruction, instKey) in careInstructions[selectedCareLanguage]"
                      :key="instKey"
                    >
                      <strong>{{ instKey }}:</strong> {{ instruction }}
                    </li>
                  </ul>
                </div>
                <div v-else>
                  <!-- Single language -->
                  <ul>
                    <li
                      v-for="(instruction, instKey) in careInstructions[careInstructionLanguages[0]]"
                      :key="instKey"
                    >
                      <strong>{{ instKey }}:</strong> {{ instruction }}
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else-if="key === 'Care Instructions'">
                <!-- Care Instructions as plain text -->
                <pre>{{ value }}</pre>
              </div>
              <span v-else>{{ value }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
export default {
  name: 'ProductDetails',
  props: {
    data: {
      type: Object,
      default: () => ({
        title: 'No title available',
        description: 'No description available.',
        clothingType: 'Unknown',
        specifications: {},
        primaryColorHex: null,
        primaryColor: 'Unknown',
        allColors: [],
        size: 'N/A',
        bulletPoints: [],
      }),
    },
    availableColors: {
      type: Array,
      default: () => [],
    },
    availableSizes: {
      type: Array,
      default: () => [],
    },
    selectedColor: {
      type: String,
      default: '',
    },
    selectedSize: {
      type: String,
      default: '',
    },
    isAfter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localSelectedColor: this.selectedColor,
      localSelectedSize: this.selectedSize,
      selectedCareLanguage: '',
    };
  },
  computed: {
    careInstructions() {
      const careInstr = this.data.specifications['Care Instructions'];
      if (!careInstr) return {};
      if (typeof careInstr === 'object') {
        const keys = Object.keys(careInstr);
        // Check if the keys are languages
        const isMultiLanguage = keys.some((key) =>
          ['english', 'german', 'french', 'italian', 'de', 'en', 'fr', 'it'].includes(key.toLowerCase())
        );
        if (isMultiLanguage) {
          // careInstr is an object with language keys
          return careInstr;
        } else {
          // careInstr is a single-language care instructions
          return { default: careInstr };
        }
      } else {
        // careInstr is not an object
        return {};
      }
    },
    hasCareInstructions() {
      return Object.keys(this.careInstructions).length > 0;
    },
    careInstructionLanguages() {
      return Object.keys(this.careInstructions);
    },
  },
  watch: {
    localSelectedColor(newColor) {
      this.$emit('colorSelected', newColor);
    },
    localSelectedSize(newSize) {
      this.$emit('sizeSelected', newSize);
    },
    selectedColor(newColor) {
      this.localSelectedColor = newColor;
    },
    selectedSize(newSize) {
      this.localSelectedSize = newSize;
    },
    careInstructions: {
      immediate: true,
      handler(newVal) {
        if (this.hasCareInstructions) {
          this.selectedCareLanguage = this.careInstructionLanguages[0];
        }
      },
    },
  },
  methods: {
    selectColor(colorValue) {
      this.localSelectedColor = colorValue;
    },
    selectSize(size) {
      this.localSelectedSize = size;
    },
    getSwatchStyle(colorValue) {
      if (this.isAfter) {
        // Assuming colorValue is a hex code
        return {
          backgroundColor: colorValue,
        };
      } else {
        // Existing logic for "Before" section
        if (colorValue.toLowerCase() === 'multicolor') {
          return {
            backgroundImage: 'linear-gradient(45deg, red, yellow, green, blue, purple)',
          };
        } else {
          const colorHex = this.getColorHex(colorValue);
          return {
            backgroundColor: colorHex,
          };
        }
      }
    },
    getColorHex(colorName) {
      // Predefined colors
      const colors = {
        red: '#FF0000',
        blue: '#0000FF',
        green: '#008000',
        purple: '#800080',
        yellow: '#FFFF00',
        white: '#FFFFFF',
        black: '#000000',
        violet: '#EE82EE',
        burgundy: '#800020',
        magenta: '#FF00FF',
        rouge: '#FF0000',
        noir: '#000000',
        pink: '#FFC0CB',
        orange: '#FFA500',
        brown: '#A52A2A',
        gray: '#808080',
        grey: '#808080',
        beige: '#F5F5DC',
        navy: '#000080',
        turquoise: '#40E0D0',
        gold: '#FFD700',
        silver: '#C0C0C0',
        bronze: '#CD7F32',
        maroon: '#800000',
        olive: '#808000',
        lime: '#00FF00',
        teal: '#008080',
        indigo: '#4B0082',
        coral: '#FF7F50',
        cyan: '#00FFFF',
        multicolor: '', // Handled separately
        // Add more colors as needed
      };
      return colors[colorName.toLowerCase()] || '#CCCCCC';
    },
  },
};
</script>
<style scoped>
.product-details {
  /* Your existing styles */
}
.product-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--primary-color);
}
.dress-category {
  font-size: 18px;
  margin-bottom: 20px;
}
.color-information {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.color-item {
  font-size: 16px;
}
.product-color {
  margin-bottom: 20px;
}
.color-swatches {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
.color-swatch {
  width: 40px;
  height: 40px;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
}
.color-swatch:hover {
  border-color: var(--accent-color);
}
.swatch-selected {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: var(--accent-color);
  color: #fff;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
}
.product-size {
  margin-bottom: 20px;
}
.size-options button {
  margin-right: 10px;
  padding: 8px 12px;
  border: 1px solid var(--primary-color);
  background-color: #fff;
  color: var(--primary-color);
  cursor: pointer;
  transition: background-color 0.3s;
}
.size-options button.selected,
.size-options button:hover {
  background-color: var(--primary-color);
  color: #fff;
}
.product-bullet-points {
  margin-bottom: 20px;
}
.product-bullet-points h2 {
  font-size: 22px;
  margin-bottom: 10px;
  color: var(--secondary-color);
}
.product-bullet-points ul {
  list-style-type: disc;
  margin-left: 20px;
}
.product-description h2 {
  font-size: 22px;
  margin-bottom: 10px;
  color: var(--secondary-color);
}
.product-description p {
  font-size: 16px;
}
.product-specifications h2 {
  font-size: 22px;
  margin-bottom: 10px;
  color: var(--secondary-color);
}
.product-specifications table {
  width: 100%;
  border-collapse: collapse;
}
.spec-key {
  width: 30%;
  font-weight: 600;
  padding: 8px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.spec-value {
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}
.spec-value pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.care-instructions {
  margin-top: 10px;
}
.care-instructions label {
  margin-right: 10px;
}
.care-instructions ul {
  list-style-type: disc;
  margin-left: 20px;
}
</style>
